import { isViewableResumeLink } from '@crm/contracts';
import { Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type CandidateRecord } from '../../lib/client.ts';
import { downloadDocument, fetchDocumentBytes, isPdf, openDocument } from '../../lib/documents.ts';
import { Button } from '../ui/button.tsx';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet.tsx';

export interface ViewableDocument {
  id: string;
  filename: string;
  contentType: string;
}

/** What the viewer shows: a name, how to get the bytes, and how to save the file. */
export interface ViewerFile {
  key: string;
  filename: string;
  load: () => Promise<ArrayBuffer>;
  download: () => Promise<void>;
}

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
const DEFAULT_ZOOM = ZOOMS.indexOf(1);

/**
 * PDF.js, imported on first use so it stays out of the main bundle (TS-1896). The worker is
 * the package's own file, served by Vite as an asset. Scripting stays off (the default).
 */
async function openPdf(data: ArrayBuffer): Promise<PDFDocumentProxy> {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs.getDocument({ data }).promise;
}

/**
 * Opening a candidate's resume or document (TS-1896): a PDF opens in the side-panel viewer,
 * any other file (doc, docx) keeps opening in a new tab, which downloads it. Render `viewer`
 * once beside whatever calls `open` / `openResumeLink`.
 */
export function useDocumentViewer(onError: (message: string) => void) {
  const [viewing, setViewing] = useState<ViewerFile | null>(null);
  return {
    /** A CRM document (S3), read through its presigned URL. */
    open: (doc: ViewableDocument) => {
      if (isPdf(doc))
        setViewing({
          key: doc.id,
          filename: doc.filename,
          load: () => fetchDocumentBytes(doc.id),
          download: () => downloadDocument(doc.id),
        });
      else void openDocument(doc.id).catch((err: unknown) => onError(describeError(err)));
    },
    /**
     * The candidate's resume link. A RecruitCRM link is read through the API (the browser
     * cannot: see `RESUME_LINK_HOSTS`) unless its filename says it is not a PDF; any other link
     * (a Drive URL) and Download open the link itself in a new tab, as before.
     */
    openResumeLink: (c: Pick<CandidateRecord, 'id' | 'resumeFileLink' | 'resumeFilename'>) => {
      const link = c.resumeFileLink;
      if (!link) return;
      const openLink = () => {
        window.open(link, '_blank', 'noopener,noreferrer');
        return Promise.resolve();
      };
      const maybePdf = !c.resumeFilename || isPdf({ contentType: '', filename: c.resumeFilename });
      if (!isViewableResumeLink(link) || !maybePdf) void openLink();
      else
        setViewing({
          key: `link:${c.id}`,
          filename: c.resumeFilename ?? 'Resume',
          load: () => api.candidates.resumeLinkBytes(c.id),
          download: openLink,
        });
    },
    viewer: <DocumentViewer file={viewing} onClose={() => setViewing(null)} />,
  };
}

/** A PDF in a side panel: every page, scrolled, with zoom and Download. */
export function DocumentViewer({
  file,
  onClose,
}: {
  file: ViewerFile | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={file !== null} onOpenChange={(o) => !o && onClose()}>
      {file && (
        <SheetContent className="max-w-4xl gap-3 overflow-hidden p-0" data-testid="document-viewer">
          <PdfView key={file.key} doc={file} />
        </SheetContent>
      )}
    </Sheet>
  );
}

function PdfView({ doc }: { doc: ViewerFile }) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const scale = ZOOMS[zoom]!;

  useEffect(() => {
    let closed = false;
    let loaded: PDFDocumentProxy | null = null;
    doc
      .load()
      .then(openPdf)
      .then(
        (p) => {
          loaded = p;
          if (closed) void p.loadingTask.destroy();
          else setPdf(p);
        },
        (err: unknown) => {
          if (!closed) setError(describeError(err));
        },
      );
    return () => {
      closed = true;
      void loaded?.loadingTask.destroy();
    };
  }, [doc]);

  return (
    <>
      <div className="border-border flex flex-wrap items-center gap-2 border-b py-3 pr-12 pl-6">
        <SheetTitle
          className="min-w-0 flex-1 truncate text-base font-semibold"
          title={doc.filename}
        >
          {doc.filename}
        </SheetTitle>
        <SheetDescription className="sr-only">Preview of {doc.filename}.</SheetDescription>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Zoom out"
          disabled={zoom === 0}
          onClick={() => setZoom((z) => z - 1)}
        >
          <ZoomOut />
        </Button>
        <span className="w-12 text-center text-sm tabular-nums" data-testid="viewer-zoom">
          {Math.round(scale * 100)}%
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Zoom in"
          disabled={zoom === ZOOMS.length - 1}
          onClick={() => setZoom((z) => z + 1)}
        >
          <ZoomIn />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void doc.download().catch((err: unknown) => setNotice(describeError(err)))}
        >
          <Download /> Download
        </Button>
        {notice && (
          <p className="text-danger w-full text-sm" role="alert">
            {notice}
          </p>
        )}
      </div>
      <div className="bg-muted min-h-0 flex-1 overflow-auto p-4" data-testid="viewer-pages">
        {error ? (
          <p className="text-danger text-sm" role="alert">
            Could not show this file: {error}. Download it instead.
          </p>
        ) : pdf ? (
          <div className="flex w-max min-w-full flex-col items-center gap-4">
            {Array.from({ length: pdf.numPages }, (_, i) => (
              <PdfPage key={i} pdf={pdf} number={i + 1} scale={scale} onError={setError} />
            ))}
          </div>
        ) : (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" /> Loading {doc.filename}…
          </div>
        )}
      </div>
    </>
  );
}

/**
 * One page on a canvas, drawn at the device's pixel ratio so text stays sharp. Every page is
 * drawn when the file opens. shortcut: fine for resumes (a few pages); draw pages as they
 * scroll into view if the viewer is ever used for long documents.
 */
function PdfPage({
  pdf,
  number,
  scale,
  onError,
}: {
  pdf: PDFDocumentProxy;
  number: number;
  scale: number;
  onError: (message: string) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let task: RenderTask | null = null;
    let cancelled = false;
    pdf
      .getPage(number)
      .then((page) => {
        const el = canvas.current;
        if (cancelled || !el) return;
        const ratio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * ratio });
        el.width = Math.floor(viewport.width);
        el.height = Math.floor(viewport.height);
        el.style.width = `${Math.floor(viewport.width / ratio)}px`;
        el.style.height = `${Math.floor(viewport.height / ratio)}px`;
        task = page.render({ canvas: el, viewport });
        return task.promise;
      })
      .catch((err: unknown) => {
        // Zooming or closing cancels a render in flight; that is not a failure.
        if (err instanceof Error && err.name === 'RenderingCancelledException') return;
        onError(describeError(err));
      });
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [pdf, number, scale, onError]);
  return (
    <canvas
      ref={canvas}
      role="img"
      aria-label={`Page ${number}`}
      className="block bg-white shadow-sm"
    />
  );
}
